import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';

const getDashboardUrl = () => process.env.REACT_APP_DASHBOARD_URL || '/dashboard/overview';

const AuthCallback = () => {
  const navigate = useNavigate();
  const { handleGoogleAuthCallback } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;

    const handleAuthCallback = async () => {
      try {
        hasProcessed.current = true;

        if (window.location.hash.includes('access_token')) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }

        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) {
          setError('Authentication failed. Please try again.');
          setTimeout(() => navigate('/'), 3000);
          return;
        }

        if (sessionData.session) {
          const result = await handleGoogleAuthCallback(sessionData.session);
          if (result.success) {
            window.location.href = getDashboardUrl();
          } else if (result.error && result.error.includes('wait')) {
            setError('Please wait a moment and try again.');
            setTimeout(() => navigate('/'), 2000);
          } else {
            setError(result.error || 'Failed to complete authentication');
            setTimeout(() => navigate('/'), 3000);
          }
          return;
        }

        // No session found initially — retry once, OAuth can need a moment.
        await new Promise(resolve => setTimeout(resolve, 2000));
        const { data: retryData } = await supabase.auth.getSession();
        if (retryData.session) {
          const result = await handleGoogleAuthCallback(retryData.session);
          if (result.success) {
            window.location.href = getDashboardUrl();
            return;
          }
        }

        // Fallback: manually parse the hash and set the session explicitly.
        if (window.location.hash.includes('access_token')) {
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          const accessToken = hashParams.get('access_token');
          const refreshToken = hashParams.get('refresh_token');

          if (accessToken) {
            const { data: manualData } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });

            if (manualData.session) {
              const result = await handleGoogleAuthCallback(manualData.session);
              if (result.success) {
                window.location.href = getDashboardUrl();
                return;
              }
            }
          }
        }

        setError('Authentication session could not be established. Please try logging in again.');
        setTimeout(() => navigate('/'), 2000);
      } catch (err) {
        setError('An unexpected error occurred');
        setTimeout(() => navigate('/'), 3000);
      } finally {
        setLoading(false);
      }
    };

    handleAuthCallback();
  }, [navigate, handleGoogleAuthCallback]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontFamily: 'inherit'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '3px solid #f0f0f0',
          borderTop: '3px solid #8b5cf6',
          borderRadius: '50%',
          animation: 'auth-callback-spin 1s linear infinite',
          marginBottom: '20px'
        }} />
        <h2>Completing your sign-in...</h2>
        <p>Please wait while we set up your account.</p>
        <style>{`@keyframes auth-callback-spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        textAlign: 'center'
      }}>
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '20px',
          maxWidth: '400px'
        }}>
          <h2 style={{ color: '#dc2626', marginBottom: '10px' }}>Authentication Error</h2>
          <p style={{ color: '#666', marginBottom: '20px' }}>{error}</p>
          <p style={{ color: '#999', fontSize: '14px' }}>Redirecting to home page...</p>
        </div>
      </div>
    );
  }

  return null;
};

export default AuthCallback;
